/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.3.1
 * @since 1.0.1
 */

import moment from 'moment';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { API_HOSTNAME, WIDGET_COSTS, WIDGET_EXPENSES_LIST_STATE } from '../../../../../modules/app/Api';
import appHistory from '../../../../../modules/app/appHistory';
import { setCookie } from '../../../../../modules/storage/storageActions';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../modules/storage/storageSelectors';
import { ECookieKey } from '../../../../../modules/storage/storageTypes';
import { CLIENT_ID, RoutePath } from '../../../../../routes/constants/routesConstants';
import { filterToJson } from '../../../../../sagas/filterToJson';
import { requestSaga } from '../../../../../sagas/requestSaga';
import { stringFormatParams } from '../../../../../utils/stringFormat';
import { EExpensesAnalyzerTypes } from '../../../../PageExpensesAnalyzer/types/pageExpensesAnalyzerTypes';
import { widgetCostsConfiguration } from './constant/widgetCostsConstants';
import { TWidgetCostsRedirectToPaymentsAction } from './types/actionsType';
import {
    widgetCostsLoadChartError,
    widgetCostsLoadChartSuccessful,
    widgetCostsRedirectToPaymentsError,
} from './widgetCostsActions';
import { makeSelectorWidgetCostsCurrentDateTo } from './widgetCostsSelectors';

/*
* -------------------------------------------
*                LOAD CHART
* -------------------------------------------
*/

export function* widgetCostsLoadChartSaga(): SagaIterator {
    const currentDate = yield select(makeSelectorWidgetCostsCurrentDateTo());

    try {
        const startDate = moment(currentDate, widgetCostsConfiguration.dateFormat)
            .startOf('month')
            .format(widgetCostsConfiguration.dateFormat);
        const selectedAccount = (yield select(makeSelectLocalStorageSelectedAccount())).id;
        const URL = API_HOSTNAME + stringFormatParams(WIDGET_COSTS, {
            accountId: selectedAccount,
            from: startDate,
            to: currentDate,
        });
        const { totalOfBasicChargesAggregates, ...chart } = yield call(requestSaga, URL, 'GET', null, {
            abortControllerId: WIDGET_COSTS,
        });
        yield put(widgetCostsLoadChartSuccessful({
            chart: {
                chart, totalOfBasicChargesAggregates,
            },
        }));
    } catch (error: any) {
        if (error?.name !== 'AbortError') {
            yield put(widgetCostsLoadChartError({ error }));
        }
    }
}

/*
* -------------------------------------------
*       REDIRECT TO PAYMENTS BASKET
* -------------------------------------------
*/

export function* widgetCostsRedirectToPaymentsSaga(action: TWidgetCostsRedirectToPaymentsAction): SagaIterator {
    const accountId = (yield select(makeSelectLocalStorageSelectedAccount())).id;
    try {
        const filterAndSort = {
            filter: {
                monthRange: {
                    valueFrom: action.payload.dateFrom,
                    valueTo: action.payload.dateTo,
                },
            },
        };
        const json = filterToJson(filterAndSort);
        yield call(requestSaga, API_HOSTNAME + WIDGET_EXPENSES_LIST_STATE(accountId), 'POST', json);
    } catch (e: any) {
        yield put(widgetCostsRedirectToPaymentsError(e));
    } finally {
        yield put(setCookie({
            key: ECookieKey.EXPENSE_ANALYZER_PAGE,
            value: EExpensesAnalyzerTypes.AccountSingle,
        }));
        appHistory.push(RoutePath.expensesAnalyzerAccountIdSubscribers.replace(CLIENT_ID, accountId));
    }
}
